import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export type GridColumn = {
  key: string;
  label: string;
  shortLabel: string;
};

export type CollapsibleGridProps<T extends Record<string, unknown>> = {
  /** Stable id for localStorage (per table). */
  tableId: string;
  columns: GridColumn[];
  rows: T[];
  /** Render cell content for a column key. */
  renderCell: (row: T, columnKey: string) => ReactNode;
  /** Optional row className (e.g. stale highlight). */
  rowClassName?: (row: T) => string;
  getRowKey: (row: T, index: number) => string;
};

const storageKey = (tableId: string) => `systemDashboard.columns.${tableId}`;

function loadCollapsed(tableId: string, columnKeys: string[]): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((k) => columnKeys.includes(String(k))).map(String));
  } catch {
    return new Set();
  }
}

function saveCollapsed(tableId: string, collapsed: Set<string>) {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify(Array.from(collapsed)));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Shared System Dashboard grid: each column header can collapse to a narrow stub
 * (short label); tap stub or expand control to reopen. Collapsed keys persist per
 * tableId in localStorage. "Reset columns" restores all.
 */
export function CollapsibleDataGrid<T extends Record<string, unknown>>({
  tableId,
  columns,
  rows,
  renderCell,
  rowClassName,
  getRowKey,
}: CollapsibleGridProps<T>) {
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setCollapsed(loadCollapsed(tableId, columnKeys));
  }, [tableId, columnKeys]);

  const persist = useCallback(
    (next: Set<string>) => {
      setCollapsed(next);
      saveCollapsed(tableId, next);
    },
    [tableId]
  );

  const toggle = useCallback(
    (key: string) => {
      const next = new Set(collapsed);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persist(next);
    },
    [collapsed, persist]
  );

  const reset = useCallback(() => {
    persist(new Set());
  }, [persist]);

  if (!rows.length) {
    return <p className="system-dashboard__empty">No rows.</p>;
  }

  return (
    <div className="system-dashboard__grid">
      <div className="system-dashboard__grid-toolbar">
        <button type="button" className="system-dashboard__reset-cols" onClick={reset}>
          Reset columns
        </button>
      </div>
      <div className="system-dashboard__table-wrap">
        <table className="system-dashboard__table">
          <thead>
            <tr>
              {columns.map((col) => {
                const isCollapsed = collapsed.has(col.key);
                return (
                  <th
                    key={col.key}
                    className={
                      isCollapsed
                        ? 'system-dashboard__th system-dashboard__th--collapsed'
                        : 'system-dashboard__th'
                    }
                    title={isCollapsed ? `Expand ${col.label}` : `Collapse ${col.label}`}
                  >
                    <button
                      type="button"
                      className="system-dashboard__col-toggle"
                      onClick={() => toggle(col.key)}
                      aria-pressed={isCollapsed}
                      aria-label={
                        isCollapsed ? `Expand column ${col.label}` : `Collapse column ${col.label}`
                      }
                    >
                      {isCollapsed ? (
                        <span className="system-dashboard__stub-label">{col.shortLabel}</span>
                      ) : (
                        <>
                          <span>{col.label}</span>
                          <span className="system-dashboard__col-chevron" aria-hidden>
                            ⟨
                          </span>
                        </>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={getRowKey(row, i)} className={rowClassName?.(row) || undefined}>
                {columns.map((col) => {
                  const isCollapsed = collapsed.has(col.key);
                  return (
                    <td
                      key={col.key}
                      className={
                        isCollapsed
                          ? 'system-dashboard__td system-dashboard__td--collapsed'
                          : 'system-dashboard__td'
                      }
                      onClick={isCollapsed ? () => toggle(col.key) : undefined}
                      title={isCollapsed ? `Expand ${col.label}` : undefined}
                    >
                      {isCollapsed ? (
                        <span className="system-dashboard__stub-label">{col.shortLabel}</span>
                      ) : (
                        renderCell(row, col.key)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Pure helpers exported for unit tests. */
export function collapsedKeysFromStorage(tableId: string, columnKeys: string[]): string[] {
  return Array.from(loadCollapsed(tableId, columnKeys));
}

export function writeCollapsedKeys(tableId: string, keys: string[]) {
  saveCollapsed(tableId, new Set(keys));
}
